import { XdmEntityObject } from '../models';
import { FieldsType } from '../constants/FieldsType';
import {
  Field,
  XdmContextInfo,
  XdmContextMappingTable,
} from '../interfaces/Field';
import { XdmRelationType } from '../constants/XdmRelationType';
import { ALL_OPTION_VALUE, getEntityPropertyName } from './xdmUtils';

export interface FieldDependencies {
  field: string;
  fieldInfo: Field;
  up: string[]; //xdm entities
}

function calculateHierarchyOfDependencies(
  xdmContextInfo: XdmContextInfo,
  xdmEntity: string,
  hierarchy: string[] = []
): string[] {
  const field = xdmContextInfo.fields.find(
    (f) => f.xdm?.entityName === xdmEntity
  );

  if (!field || !field.xdm) {
    return hierarchy;
  }

  const {
    xdm: { xdmRelation, entityName },
  } = field;

  if (!xdmRelation || !entityName) {
    return hierarchy;
  }

  if (xdmRelation.type == XdmRelationType.Direct && !!xdmRelation.relation) {
    return calculateHierarchyOfDependencies(
      xdmContextInfo,
      xdmRelation.relation.relatedEntity,
      [...hierarchy, xdmRelation.relation.relatedEntity]
    );
  }

  if (
    xdmRelation.type == XdmRelationType.Mapping &&
    !!xdmRelation.mappingTable
  ) {
    const mappingTable = xdmContextInfo.mappingTables.find(
      (m) => m.entityName === xdmRelation.mappingTable
    );

    if (!mappingTable) {
      return hierarchy;
    }

    const parentEntity = mappingTable.mappings.find(
      (m) => m.relatedEntity !== entityName
    );

    return !!parentEntity
      ? calculateHierarchyOfDependencies(
          xdmContextInfo,
          parentEntity.relatedEntity,
          [...hierarchy, parentEntity.relatedEntity]
        )
      : [];
  }

  return hierarchy;
}

function calculateFieldsDependencies(xdmContextInfo: XdmContextInfo) {
  const { fields } = xdmContextInfo;

  return fields
    .map<FieldDependencies | null>((field) => {
      if (field.type != FieldsType.xdm || !field.xdm) {
        return null;
      }

      const {
        xdm: { entityName },
        name,
      } = field;

      return {
        field: name,
        fieldInfo: field,
        up: calculateHierarchyOfDependencies(xdmContextInfo, entityName),
      };
    })
    .reduce((prev, curr) => {
      if (curr) {
        return [...prev, curr];
      }

      return prev;
    }, [] as FieldDependencies[]);
}

function isAllOptionSelected(documentValues: any[], fieldName: string) {
  return (
    documentValues.length === 1 &&
    documentValues[0][fieldName] === ALL_OPTION_VALUE
  );
}

function filterXdmData(
  xdmData: XdmEntityObject[],
  dataFiter: ((xdmEntity: XdmEntityObject) => boolean) | null
) {
  if (!dataFiter) {
    return xdmData;
  }

  return xdmData.filter(dataFiter);
}

function buildXdmDataFilterForDirectRelation(
  field: FieldDependencies,
  fieldsDependencies: FieldDependencies[],
  documentValues: any,
  xdmEntitiesData: { [key: string]: XdmEntityObject[] },
  xdmDataFilters: {
    [field: string]: ((xdmEntity: XdmEntityObject) => boolean) | null;
  }
) {
  const xdmRelationInfo = field.fieldInfo.xdm?.xdmRelation?.relation;
  if (!xdmRelationInfo) {
    throw new Error('xdm relation is not correctly configured');
  }

  const parentEntityField = fieldsDependencies.find(
    (f) => f.fieldInfo.xdm?.entityName === xdmRelationInfo.relatedEntity
  );

  if (!parentEntityField) {
    throw new Error('xdm direct relation - parent field not found');
  }

  const parentEntityXdmFieldName = xdmRelationInfo.relatedField;
  if (!parentEntityXdmFieldName) {
    throw new Error(
      'xdm relation is configured wrong, parentEntityXdmFieldName not found'
    );
  }

  const parentFieldMappings = parentEntityField.fieldInfo.xdm?.mappings;
  if (!parentFieldMappings) {
    throw new Error(
      'xdm relation is configured wrong, parentFieldMappings not found'
    );
  }

  const parentEntityFieldName = getEntityPropertyName(
    parentFieldMappings,
    parentEntityXdmFieldName
  );

  if (!parentEntityFieldName) {
    throw new Error(
      'xdm relation is configured wrong, parentEntityFieldName not found'
    );
  }

  const currentValueOfParentField = documentValues[parentEntityField.field];
  if (!currentValueOfParentField || currentValueOfParentField.length === 0) {
    return () => false;
  }

  const fkKey = xdmRelationInfo.foreignKeyField;

  if (parentEntityField.fieldInfo.isArray) {
    let fkValues: any = [];

    if (isAllOptionSelected(currentValueOfParentField, parentEntityFieldName)) {
      const xdmDataFiltered = filterXdmData(
        xdmEntitiesData[xdmRelationInfo.relatedEntity],
        xdmDataFilters[parentEntityField.field]
      );

      fkValues = xdmDataFiltered.map(
        (c: XdmEntityObject) => c[parentEntityXdmFieldName]
      ) as string[];
    } else {
      fkValues = currentValueOfParentField.map(
        (c: XdmEntityObject) => c[parentEntityFieldName]
      ) as string[];
    }

    return (xdmEntity: XdmEntityObject) => {
      return fkValues.includes(xdmEntity[fkKey]);
    };
  }

  return (xdmEntity: XdmEntityObject) => {
    return (
      xdmEntity[fkKey] === currentValueOfParentField[parentEntityFieldName]
    );
  };
}

function buildXdmDataFilterForMappingRelation(
  field: FieldDependencies,
  fieldsDependencies: FieldDependencies[],
  documentValues: any,
  mappingTables: XdmContextMappingTable[],
  xdmEntitiesData: { [key: string]: XdmEntityObject[] },
  xdmDataFilters: {
    [field: string]: ((xdmEntity: XdmEntityObject) => boolean) | null;
  }
) {
  const xdmRelation = field.fieldInfo.xdm?.xdmRelation;
  if (!xdmRelation) {
    throw new Error('xdm relation is not correctly configured');
  }

  const mappingTable = mappingTables.find(
    (m) => m.entityName === xdmRelation.mappingTable
  );

  if (!mappingTable) {
    throw new Error('xdm relation is not set up correctly');
  }

  const parentEntityRelationInfo = mappingTable.mappings.find(
    (m) => m.relatedEntity !== field.fieldInfo.xdm?.entityName
  );
  const currentEntityRelationInfo = mappingTable.mappings.find(
    (m) => m.relatedEntity === field.fieldInfo.xdm?.entityName
  );

  if (!parentEntityRelationInfo || !currentEntityRelationInfo) {
    throw new Error('xdm relation is not set up correctly');
  }

  const parentEntityField = fieldsDependencies.find(
    (f) =>
      f.fieldInfo.xdm?.entityName === parentEntityRelationInfo.relatedEntity
  ) as FieldDependencies;

  if (
    !documentValues[parentEntityField.field] ||
    documentValues[parentEntityField.field].length === 0
  ) {
    return () => false;
  }

  const parentEntityPropName = getEntityPropertyName(
    parentEntityField.fieldInfo.xdm?.mappings as { [key: string]: string },
    parentEntityRelationInfo.relatedField
  ) as string;

  let parentEntitySelections: any = [];
  if (parentEntityField.fieldInfo.xdm?.isArray) {
    if (
      isAllOptionSelected(
        documentValues[parentEntityField.field],
        parentEntityPropName
      )
    ) {
      const parentXdmEntityFilteredData = filterXdmData(
        xdmEntitiesData[parentEntityRelationInfo.relatedEntity],
        xdmDataFilters[parentEntityField.field]
      );

      parentEntitySelections = parentXdmEntityFilteredData.map(
        (c: XdmEntityObject) => c[parentEntityRelationInfo.relatedField]
      ) as string[];
    } else {
      parentEntitySelections = (
        documentValues[parentEntityField.field] ?? []
      ).map((i: any) => i[parentEntityPropName]);
    }
  } else {
    parentEntitySelections = [documentValues[parentEntityField.field]].map(
      (i: any) => i[parentEntityPropName]
    );
  }

  const currentEntityFilteredItems = xdmEntitiesData[mappingTable.entityName]
    .filter((i) =>
      parentEntitySelections.includes(
        i[parentEntityRelationInfo.foreignKeyField]
      )
    )
    .map((i) => i[currentEntityRelationInfo.foreignKeyField]);

  return (xdmEntity: XdmEntityObject) => {
    return currentEntityFilteredItems.includes(
      xdmEntity[currentEntityRelationInfo.relatedField]
    );
  };
}

function getFieldsXdmDataFilters(
  xdmContextInfo: XdmContextInfo,
  fieldsDependencies: FieldDependencies[],
  currentValues: any,
  xdmEntitiesData: { [key: string]: XdmEntityObject[] }
): { [field: string]: ((xdmEntity: XdmEntityObject) => boolean) | null } {
  return fieldsDependencies.reduce((prev, curr) => {
    if (curr.up.length === 0) {
      return {
        ...prev,
        [curr.field]: null,
      };
    }

    if (curr.fieldInfo.xdm?.xdmRelation?.type === XdmRelationType.Direct) {
      return {
        ...prev,
        [curr.field]: buildXdmDataFilterForDirectRelation(
          curr,
          fieldsDependencies,
          currentValues,
          xdmEntitiesData,
          prev
        ),
      };
    }

    if (curr.fieldInfo.xdm?.xdmRelation?.type === XdmRelationType.Mapping) {
      return {
        ...prev,
        [curr.field]: buildXdmDataFilterForMappingRelation(
          curr,
          fieldsDependencies,
          currentValues,
          xdmContextInfo.mappingTables,
          xdmEntitiesData,
          prev
        ),
      };
    }

    return prev ?? {};
  }, {});
}

export { calculateFieldsDependencies, getFieldsXdmDataFilters };
